// Dynamic Table options (rendered inside the properties panel when a DynamicTable
// is selected, ABOVE the shared table structure/cell-styling sections). The table
// itself is edited on canvas (double-click a cell to type); this section covers
// the data-binding semantics: row roles, data path, repeat header, alternating
// fill, and click-to-insert field chips from the imported dataset.
import { DynamicTable, setSelectedCellText } from './../widgets/dynamic-table'
import { addRow, removeRow } from './../widgets/table-widget'
import { state } from './../state'
import { toast } from './../ui/dom'

type Fn = () => void

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (cls) e.className = cls
  if (text != null) e.textContent = text
  return e
}
function row(label: string, control: HTMLElement): HTMLElement {
  const r = el('label', 'prop-row flex items-center gap-2')
  const l = el('span', 'prop-label text-[10px] text-slate-500', label); l.style.minWidth = '70px'
  r.append(l, control); return r
}

export function renderDynamicTablePanel(body: HTMLElement, t: DynamicTable, render: Fn, commit: Fn): void {
  const sec = el('div')
  sec.append(el('div', 'prop-label', 'Dynamic Table'))

  const hint = el('div', 'text-[10px] text-slate-500 leading-relaxed mb-2')
  hint.innerHTML =
    'Row 1 = <strong class="text-slate-300">header</strong> · row 2 <strong class="text-slate-300">repeats for each data row</strong> ' +
    '(put <code>{{field}}</code> in its cells) · rows below = <strong class="text-slate-300">summary</strong>, e.g. ' +
    '<code>{{ rows | sum: "amount" | money }}</code>. Double-click a cell to edit it.'
  sec.append(hint)

  // Data path (which array in the dataset the template row repeats over).
  const path = el('input', 'prop-input flex-1') as HTMLInputElement
  path.type = 'text'; path.value = t.binding?.path ?? 'rows'; path.placeholder = 'rows'
  path.addEventListener('change', () => { t.binding = { path: path.value.trim() || 'rows' }; commit() })
  sec.append(row('Data path', path))

  // Repeat header on overflow pages.
  const rep = el('input') as HTMLInputElement
  rep.type = 'checkbox'; rep.checked = t.repeatHeader; rep.style.accentColor = '#3b82f6'
  rep.addEventListener('change', () => { t.repeatHeader = rep.checked; commit() })
  sec.append(row('Repeat header', rep))

  // Alternating row fill (PDF only — the editor shows the template row once).
  const altWrap = el('div', 'flex items-center gap-2')
  const altOn = el('input') as HTMLInputElement
  altOn.type = 'checkbox'; altOn.checked = !!t.altRowFill; altOn.style.accentColor = '#3b82f6'
  const altColor = el('input') as HTMLInputElement
  altColor.type = 'color'; altColor.value = t.altRowFill ?? '#f1f5f9'; altColor.disabled = !t.altRowFill
  altColor.className = 'w-7 h-7 rounded cursor-pointer bg-transparent'
  const apply = () => { t.altRowFill = altOn.checked ? altColor.value : undefined; altColor.disabled = !altOn.checked; commit() }
  altOn.addEventListener('change', apply)
  altColor.addEventListener('change', apply)
  altWrap.append(altOn, altColor)
  sec.append(row('Stripe rows', altWrap))

  // Summary row structure.
  const sumBtns = el('div', 'flex gap-2')
  const mk = (label: string, title: string, fn: Fn) => {
    const b = el('button', 'tb-btn text-xs', label) as HTMLButtonElement
    b.type = 'button'; b.title = title
    b.style.cssText = 'background:#1e293b;border:1px solid #334155;padding:4px 10px'
    b.addEventListener('click', fn)
    return b
  }
  sumBtns.append(
    mk('+ Summary row', 'Add a summary row below the data', () => { addRow(t); render(); commit() }),
    mk('− Summary row', 'Remove the last summary row', () => {
      if (t.rows <= 2) { toast('Header + template rows are required'); return }
      removeRow(t); render(); commit()
    }),
  )
  sec.append(row('Summary', sumBtns))

  // Field chips — click to write {{field}} into the selected cell.
  if (state.batchHeaders?.length) {
    const chips = el('div', 'flex flex-wrap gap-1.5 mt-1')
    for (const h of state.batchHeaders) {
      const chip = el('button', 'batch-field-tag matched', `{{${h}}}`) as HTMLButtonElement
      chip.type = 'button'
      chip.title = `Insert {{${h}}} into the selected cell`
      chip.addEventListener('click', () => {
        if (!setSelectedCellText(t, `{{${h}}}`)) toast('Click a cell in the table first')
        else commit()
      })
      chips.append(chip)
    }
    sec.append(el('div', 'prop-label text-[10px] text-slate-500 mt-2', 'Insert field into selected cell'))
    sec.append(chips)
  } else {
    sec.append(el('div', 'text-[10px] text-slate-600 mt-1', 'Tip: Import Data to get click-to-insert field chips here.'))
  }

  body.append(sec)
}
